import { FlatList, type FlatListProps } from "react-native";

import type { ListProps } from "@/components/ui/FlashList.types";

export type { ListProps, ListRef } from "@/components/ui/FlashList.types";

/**
 * The web build's list: React Native's `FlatList`, which mounts a window of cells in document
 * order and never moves a row's content between views.
 *
 * On the web, document order is the reading order for a screen reader and the tab key, and
 * FlashList's recycling does not preserve it (see `FlashList.tsx`). The desktop inventory table is
 * a `role="grid"` carrying `aria-rowindex` (#785), and the customer storefront is a public list
 * read with assistive tech, so both need DOM order. Native keeps the recycling, because its
 * accessibility tree is ordered correctly regardless.
 *
 * `@shopify/flash-list` is not imported here, and `ListProps` arrives as a type-only import, so
 * Metro leaves the package out of the web bundle.
 *
 * `className` passes straight through: NativeWind already registers `VirtualizedList`, so FlatList
 * resolves it without the wrapper `View` the native build uses. That also keeps the rendered DOM
 * the web journeys assert against.
 */
export function FlashList<ItemT>({
  // FlashList's `{ disabled }` shape means nothing to FlatList, which reads `minIndexForVisible`
  // and anchors nothing without it. Dropped so the native-only option cannot reach it.
  maintainVisibleContentPosition: _nativeOnly,
  ...props
}: ListProps<ItemT>) {
  // The two vendor prop types are nominally unrelated, but every prop the call sites pass exists in
  // both. This cast is the one place they are bridged, and `ListProps` stops a call site from
  // passing anything both lists cannot honor.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- see above; the bridge is this line alone.
  return <FlatList {...(props as unknown as FlatListProps<ItemT>)} />;
}
